import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { OrderAuditModel, OrderAuditSearchParams, OrderAuditResponse, OrderAuditActionRequest, OrderAuditActionResponse } from './order-audit.model';

@Injectable({
  providedIn: 'root'
})
export class OrderAuditService {
  // private http = inject(HttpClient);
  
  // Mock data for demonstration
  private mockOrders: OrderAuditModel[] = [
    {
      id: '1',
      thirdPartyOrderNumber: 'TB20241201001',
      internalOrderNumber: 'INT20241201001',
      tags: 'VIP',
      orderSource: 'Taobao',
      auditStatus: 'First Review Pending',
      productLineCount: 3,
      salesStore: 'Store A',
      buyerNickname: 'buyer001',
      salesChannel: 'Online',
      buyerNotes: 'Please deliver before 5pm',
      merchantNotes: 'Special handling required',
      recipientName: 'John Doe',
      recipientPhone: '13800138000',
      recipientAddress: 'HCM City, Vietnam',
      shipmentStatus: 'Pending',
      receiptStatus: 'Pending',
      totalProductAmount: 299.99,
      buyerPayable: 299.99,
      totalProductCost: 150.00,
      thirdPartyOrderTime: new Date('2024-12-01T10:30:00'),
      paymentTime: new Date('2024-12-01T10:35:00'),
      internalCreateTime: new Date('2024-12-01T11:00:00'),
      expectedShipmentTime: new Date('2024-12-02T14:00:00'),
      preemptionStatus: 'Pending',
      sourcingStatus: 'In Progress',
      suspensionStatus: 'Normal',
      preemptionWritebackStatus: 'Pending',
      shipmentWritebackStatus: 'Pending',
      hasGifts: 'Yes'
    },
    {
      id: '2',
      thirdPartyOrderNumber: 'TM20241201002',
      internalOrderNumber: 'INT20241201002',
      tags: 'Normal',
      orderSource: 'Tmall',
      auditStatus: 'Second Review Pending',
      productLineCount: 1,
      salesStore: 'Store B',
      buyerNickname: 'buyer002',
      salesChannel: 'Online',
      buyerNotes: '',
      merchantNotes: '',
      recipientName: 'Jane Smith',
      recipientPhone: '13900139000',
      recipientAddress: 'HCM City, Vietnam',
      shipmentStatus: 'Pending',
      receiptStatus: 'Pending',
      totalProductAmount: 159.99,
      buyerPayable: 159.99,
      totalProductCost: 80.00,
      thirdPartyOrderTime: new Date('2024-12-01T14:20:00'),
      paymentTime: new Date('2024-12-01T14:25:00'),
      internalCreateTime: new Date('2024-12-01T15:00:00'),
      expectedShipmentTime: new Date('2024-12-02T16:00:00'),
      preemptionStatus: 'Completed',
      sourcingStatus: 'Completed',
      suspensionStatus: 'Normal',
      preemptionWritebackStatus: 'Completed',
      shipmentWritebackStatus: 'Pending',
      hasGifts: 'No'
    },
    {
      id: '3',
      thirdPartyOrderNumber: 'JD20241201003',
      internalOrderNumber: 'INT20241201003',
      tags: 'Urgent',
      orderSource: 'JD',
      auditStatus: 'Review Rejected',
      productLineCount: 5,
      salesStore: 'Store C',
      buyerNickname: 'buyer003',
      salesChannel: 'Online',
      buyerNotes: 'Urgent delivery needed',
      merchantNotes: 'Stock shortage',
      recipientName: 'Mike Johnson',
      recipientPhone: '13700137000',
      recipientAddress: 'HCM City, Vietnam',
      shipmentStatus: 'Pending',
      receiptStatus: 'Pending',
      totalProductAmount: 599.99,
      buyerPayable: 599.99,
      totalProductCost: 300.00,
      thirdPartyOrderTime: new Date('2024-12-01T16:45:00'),
      paymentTime: new Date('2024-12-01T16:50:00'),
      internalCreateTime: new Date('2024-12-01T17:00:00'),
      expectedShipmentTime: new Date('2024-12-02T18:00:00'),
      preemptionStatus: 'Failed',
      sourcingStatus: 'Failed',
      suspensionStatus: 'Suspended',
      preemptionWritebackStatus: 'Failed',
      shipmentWritebackStatus: 'Pending',
      hasGifts: 'Yes'
    }
  ];

  constructor() {}

  getOrders(params: OrderAuditSearchParams): Observable<OrderAuditResponse> {
    // Simulate API call with delay
    return of({
      data: this.mockOrders,
      total: this.mockOrders.length,
      page: Math.floor((params.first || 0) / (params.rows || 20)) + 1,
      pageSize: params.rows || 20
    });
  }

  approveOrders(orderIds: string[]): Observable<OrderAuditActionResponse> {
    // Simulate API call
    return of({
      success: true,
      message: 'Orders approved successfully',
      processedCount: orderIds.length,
      failedCount: 0
    }).pipe(delay(1000));
  }

  rejectOrders(orderIds: string[]): Observable<OrderAuditActionResponse> {
    // Simulate API call
    return of({
      success: true,
      message: 'Orders rejected successfully',
      processedCount: orderIds.length,
      failedCount: 0
    }).pipe(delay(1000));
  }

  exportOrders(params: OrderAuditSearchParams): Observable<Blob> {
    // Simulate file download
    const csvContent = this.generateCSV(this.mockOrders);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    return of(blob).pipe(delay(500));
  }

  private generateCSV(orders: OrderAuditModel[]): string {
    const headers = [
      'Serial Number',
      'Third Party Order Number',
      'Internal Order Number',
      'Tags',
      'Order Source',
      'Audit Status',
      'Product Line Count',
      'Sales Store',
      'Buyer Nickname',
      'Sales Channel',
      'Buyer Notes',
      'Merchant Notes',
      'Recipient Name',
      'Recipient Phone',
      'Recipient Address',
      'Shipment Status',
      'Receipt Status',
      'Total Product Amount',
      'Buyer Payable',
      'Total Product Cost',
      'Third Party Order Time',
      'Payment Time',
      'Internal Create Time',
      'Expected Shipment Time',
      'Preemption Status',
      'Sourcing Status',
      'Suspension Status',
      'Preemption Writeback Status',
      'Shipment Writeback Status',
      'Has Gifts'
    ];

    const rows = orders.map(order => [
      order.thirdPartyOrderNumber,
      order.internalOrderNumber,
      order.tags,
      order.orderSource,
      order.auditStatus,
      order.productLineCount,
      order.salesStore,
      order.buyerNickname,
      order.salesChannel,
      order.buyerNotes,
      order.merchantNotes,
      order.recipientName,
      order.recipientPhone,
      order.recipientAddress,
      order.shipmentStatus,
      order.receiptStatus,
      order.totalProductAmount,
      order.buyerPayable,
      order.totalProductCost,
      order.thirdPartyOrderTime.toISOString(),
      order.paymentTime.toISOString(),
      order.internalCreateTime.toISOString(),
      order.expectedShipmentTime.toISOString(),
      order.preemptionStatus,
      order.sourcingStatus,
      order.suspensionStatus,
      order.preemptionWritebackStatus,
      order.shipmentWritebackStatus,
      order.hasGifts
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }

  // Real API methods (commented out for now)
  /*
  private apiUrl = '/api/order-audit';

  getOrders(params: OrderAuditSearchParams): Observable<OrderAuditResponse> {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key as keyof OrderAuditSearchParams];
      if (value !== undefined && value !== null && value !== '') {
        if (value instanceof Date) {
          httpParams = httpParams.set(key, value.toISOString());
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return this.http.get<OrderAuditResponse>(`${this.apiUrl}/orders`, { params: httpParams });
  }

  approveOrders(orderIds: string[]): Observable<OrderAuditActionResponse> {
    const request: OrderAuditActionRequest = { orderIds };
    return this.http.post<OrderAuditActionResponse>(`${this.apiUrl}/approve`, request);
  }

  rejectOrders(orderIds: string[]): Observable<OrderAuditActionResponse> {
    const request: OrderAuditActionRequest = { orderIds };
    return this.http.post<OrderAuditActionResponse>(`${this.apiUrl}/reject`, request);
  }

  exportOrders(params: OrderAuditSearchParams): Observable<Blob> {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key as keyof OrderAuditSearchParams];
      if (value !== undefined && value !== null && value !== '') {
        if (value instanceof Date) {
          httpParams = httpParams.set(key, value.toISOString());
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return this.http.get(`${this.apiUrl}/export`, { 
      params: httpParams,
      responseType: 'blob' 
    });
  }
  */
} 